// DirectInseminationForm.js
import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Typography,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Snackbar,
  Alert,
  IconButton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
const apiUrl = process.env.REACT_APP_API_URL;
const user_id = localStorage.getItem('userId');

const DirectInseminationForm = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    horse_origin: 'Inside',
    inside_horse_id: '',
    horse_name: '',
    rfid_chip_number: '',
    stallion_id: '',
    insemination_date: new Date().toISOString().split('T')[0],
    semen_source_type: 'In-House',
    semen_type: 'Fresh',
    owner_id: '',
    notes: '',
    currency: 'SAR',
    created_by: user_id,
  });

  const [success, setSuccess] = useState(false);
  const [mares, setMares] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [procedureOptions, setProcedureOptions] = useState([]);
  const [stallions, setStallions] = useState([]);

  useEffect(() => {
    axios
      .get(`${apiUrl}/stallionslist`)
      .then((res) => setStallions(res.data))
      .catch((err) => console.error('Error fetching stallions:', err));
  }, []);

  useEffect(() => {
    const selected = mares.find((m) => m.horse_id === formData.inside_horse_id);
    if (selected?.owner_id) {
      setFormData((prev) => ({
        ...prev,
        owner_id: selected.owner_id.toString(), // نحوله لنص لتجنب أخطاء في الـ input، ثم نحوله لرقم قبل الإرسال
      }));
    }
  }, [formData.inside_horse_id, mares]);

  useEffect(() => {
    if (formData.horse_origin === 'Inside') {
      axios
        .get(`${apiUrl}/mares`)
        .then((res) => setMares(res.data))
        .catch((err) => console.error('Error fetching mares:', err));
    }
  }, [formData.horse_origin]);

  useEffect(() => {
    axios
      .get(`${apiUrl}/reproduction-clinic/procedures`)
      .then((res) => setProcedureOptions(res.data))
      .catch((err) => console.error('Error fetching procedures:', err));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleProcedureChange = (index, field, value) => {
    const updated = [...procedures];
    if (field === 'procedure_id') {
      const selected = procedureOptions.find((p) => p.procedure_id === value);
      const exists = procedures.some(
        (p, i) => p.procedure_id === value && i !== index
      );
      if (exists) return; // Prevent duplicates
      updated[index]['procedure_id'] = value;
      updated[index]['custom_price'] = selected?.default_price || 0;
      updated[index]['description'] = selected?.description || '';
    } else {
      updated[index][field] = value;
    }
    setProcedures(updated);
  };

  const addProcedure = () => {
    setProcedures((prev) => [
      ...prev,
      { procedure_id: '', custom_price: '', description: '' },
    ]);
  };

  const removeProcedure = (index) => {
    const updated = procedures.filter((_, i) => i !== index);
    setProcedures(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const total_cost = procedures.reduce(
        (sum, p) => sum + parseFloat(p.custom_price || 0),
        0
      );
      const payload = {
        ...formData,
        procedures,
        total_cost,
        owner_id: parseInt(formData.owner_id) || null, // نحوله لرقم أو null
      };

      await axios.post(`${apiUrl}/insemination/create`, payload);
      setSuccess(true);
      setFormData((prev) => ({
        ...prev,
        horse_name: '',
        rfid_chip_number: '',
        insemination_date: new Date().toISOString().split('T')[0],
        notes: '',
      }));
      setProcedures([]);

      setTimeout(() => {
        navigate('/ReproductionPage');
      }, 1000);
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const totalCost = procedures.reduce(
    (sum, p) => sum + parseFloat(p.custom_price || 0),
    0
  );

  return (
    <Box
      sx={{
        maxWidth: 800,
        mx: 'auto',
        mt: 4,
        direction: i18n.language === 'ar' ? 'rtl' : 'ltr',
      }}
    >
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" fontWeight="bold">
          {t('directInsemination.title')}
        </Typography>
      </Box>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>{t('directInsemination.horseOrigin')}</InputLabel>
              <Select
                name="horse_origin"
                value={formData.horse_origin}
                onChange={handleChange}
              >
                <MenuItem value="Inside">
                  {t('directInsemination.inside')}
                </MenuItem>
                <MenuItem value="Outside">
                  {t('directInsemination.outside')}
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {formData.horse_origin === 'Inside' ? (
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>{t('directInsemination.horseId')}</InputLabel>
                <Select
                  name="inside_horse_id"
                  value={formData.inside_horse_id}
                  onChange={handleChange}
                >
                  {mares.map((mare) => (
                    <MenuItem key={mare.horse_id} value={mare.horse_id}>
                      {mare.name} - {mare.rfid_chip_number}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          ) : (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('directInsemination.horseName')}
                  name="horse_name"
                  value={formData.horse_name}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('directInsemination.rfid')}
                  name="rfid_chip_number"
                  value={formData.rfid_chip_number}
                  onChange={handleChange}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label={t('directInsemination.date')}
              name="insemination_date"
              InputLabelProps={{ shrink: true }}
              value={formData.insemination_date}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>{t('directInsemination.stallionId')}</InputLabel>
              <Select
                name="stallion_id"
                value={formData.stallion_id}
                onChange={handleChange}
              >
                {stallions.map((stallion) => (
                  <MenuItem
                    key={stallion.stallion_id}
                    value={stallion.stallion_id}
                  >
                    {stallion.name} | {stallion.name_ar}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>{t('directInsemination.semenType')}</InputLabel>
              <Select
                name="semen_type"
                value={formData.semen_type}
                onChange={handleChange}
              >
                <MenuItem value="Fresh">
                  {t('directInsemination.fresh')}
                </MenuItem>
                <MenuItem value="Frozen">
                  {t('directInsemination.frozen')}
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>{t('directInsemination.semenSource')}</InputLabel>
              <Select
                name="semen_source_type"
                value={formData.semen_source_type}
                onChange={handleChange}
              >
                <MenuItem value="In-House">
                  {t('directInsemination.inHouse')}
                </MenuItem>
                <MenuItem value="External">
                  {t('directInsemination.external')}
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* الإجراءات */}
          <Grid item xs={12}>
            <Typography variant="h6">
              {t('directInsemination.procedures')}
            </Typography>
            {procedures.map((proc, index) => (
              <Grid container spacing={1} key={index} alignItems="center">
                <Grid item xs={8}>
                  <FormControl fullWidth>
                    <InputLabel>{t('directInsemination.procedure')}</InputLabel>
                    <Select
                      value={proc.procedure_id}
                      onChange={(e) =>
                        handleProcedureChange(
                          index,
                          'procedure_id',
                          e.target.value
                        )
                      }
                    >
                      {procedureOptions.map((opt) => (
                        <MenuItem
                          key={opt.procedure_id}
                          value={opt.procedure_id}
                        >
                          {opt.procedure_name} | {opt.description}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {false && (
                  <>
                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        type="number"
                        label={t('directInsemination.price')}
                        value={proc.custom_price}
                        onChange={(e) =>
                          handleProcedureChange(
                            index,
                            'custom_price',
                            e.target.value
                          )
                        }
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={1}>
                  <IconButton
                    color="error"
                    onClick={() => removeProcedure(index)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
            <Button variant="outlined" onClick={addProcedure} sx={{ mt: 2 }}>
              {t('directInsemination.addProcedure')}
            </Button>

            {false && (
              <>
                <Typography variant="subtitle1" sx={{ mt: 2 }}>
                  {t('directInsemination.total')}: {totalCost}{' '}
                  {formData.currency}
                </Typography>
              </>
            )}
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label={t('directInsemination.notes')}
              name="notes"
              value={formData.notes}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button variant="contained" type="submit">
                {t('directInsemination.submit')}
              </Button>
              <Button
                variant="outlined"
                component={RouterLink}
                to="/ReproductionPage"
              >
                {t('back')}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>

      <Snackbar
        open={success}
        autoHideDuration={4000}
        onClose={() => setSuccess(false)}
      >
        <Alert
          onClose={() => setSuccess(false)}
          severity="success"
          variant="filled"
        >
          {t('directInsemination.success')}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DirectInseminationForm;
